import { useQuery } from '@tanstack/react-query';
import { LEVELS, TOTAL_LESSONS } from '@notion-training/shared';
import type { SessionUser, UserProgress, GetPlanResponse, AIPlan } from '@notion-training/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { PlanSection } from '../components/plan/PlanSection.js';

type MeResponse = { user: SessionUser; progress: UserProgress };

export function Dashboard() {
  const { state } = useAuth();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/me'),
  });
  const planQuery = useQuery<GetPlanResponse>({
    queryKey: ['plan'],
    queryFn: () => api.get<GetPlanResponse>('/api/plan'),
  });
  type LessonRow = { slug: string; level: number; order: number; title: string };
  type LessonsResp = { lessons: LessonRow[] };
  const lessonsQuery = useQuery<LessonsResp>({
    queryKey: ['lessons'],
    queryFn: () => api.get<LessonsResp>('/api/lessons'),
  });

  const progress = meQuery.data?.progress;
  const completionPct = progress ? Math.round((progress.lessonsCompleted / TOTAL_LESSONS) * 100) : 0;
  const plan = planQuery.data?.plan ?? null;

  return (
    <div className="view" style={{ maxWidth: 1240, margin: '0 auto', padding: '36px 32px 96px' }}>
      <div style={{ marginBottom: 24 }}>
        <div className="eyebrow">▌ Your dashboard</div>
        <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', margin: '6px 0 0' }}>
          Welcome back{state.status === 'signed-in' ? `, ${state.user.name.split(' ')[0]}` : ''}.
        </h1>
        <p style={{ color: 'var(--color-text-3)', marginTop: 6, fontSize: 13 }}>
          {progress?.lessonsCompleted ?? 0} of {TOTAL_LESSONS} lessons · streak {progress?.streakDays ?? 0} days
        </p>
      </div>

      <PlanSection />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        <Stat label="Lessons done" value={String(progress?.lessonsCompleted ?? 0)} suffix={`of ${TOTAL_LESSONS}`} />
        <Stat label="Completion" value={`${completionPct}%`} />
        <Stat label="Streak · current" value={String(progress?.streakDays ?? 0)} suffix="day" />
        <Stat label="Level" value={String(progress?.currentLevel ?? 0)} />
      </div>

      <LevelProgress
        plan={plan}
        completedSlugs={progress?.completedSlugs ?? []}
        lessons={lessonsQuery.data?.lessons ?? []}
      />
    </div>
  );
}

function LevelProgress({
  plan,
  completedSlugs,
  lessons,
}: {
  plan: AIPlan | null;
  completedSlugs: string[];
  lessons: Array<{ slug: string; level: number }>;
}) {
  const tagOf = (id: number): 'priority' | 'useful' | 'skip' | null => {
    if (!plan) return null;
    return plan.levels.find((l) => l.level === id)?.tag ?? null;
  };
  const reasonOf = (id: number): string | null => {
    if (!plan) return null;
    return plan.levels.find((l) => l.level === id)?.reason ?? null;
  };

  const countsByLevel = (id: number): { completed: number; total: number } => {
    const inLevel = lessons.filter((l) => l.level === id);
    const total = LEVELS.find((l) => l.id === id)?.expectedLessons ?? inLevel.length;
    const completedInLevel = inLevel.filter((l) => completedSlugs.includes(l.slug)).length;
    return { completed: completedInLevel, total };
  };

  return (
    <div className="card" style={{ padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h3 style={{ fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-3)', fontWeight: 600, margin: 0 }}>
          Level Progress
        </h3>
        {plan ? (
          <div style={{ display: 'flex', gap: 6, fontSize: 9.5 }}>
            <Chip tone="accent">PRIORITY</Chip>
            <Chip tone="neutral">USEFUL</Chip>
            <Chip tone="dim">SKIP</Chip>
          </div>
        ) : (
          <span className="chip" style={{ fontSize: 10 }}>{LEVELS.length} levels</span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {LEVELS.map((level) => {
          const tag = tagOf(level.id);
          const reason = reasonOf(level.id);
          const dimmed = tag === 'skip';
          const accented = tag === 'priority';

          return (
            <div
              key={level.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '24px 1fr auto',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid var(--color-border)',
                opacity: dimmed ? 0.45 : 1,
                ...(accented
                  ? { borderLeft: '2px solid var(--color-accent)', paddingLeft: 8, marginLeft: -10 }
                  : {}),
              }}
            >
              <span style={{ fontSize: 15 }}>{level.emoji}</span>
              <div>
                <div style={{
                  fontSize: 12.5,
                  color: accented ? 'var(--color-text)' : 'var(--color-text-2)',
                  fontWeight: accented ? 600 : 400,
                }}>
                  {level.shortTitle}{' '}
                  <span style={{ color: 'var(--color-text-3)', fontWeight: 400 }}>
                    — {countsByLevel(level.id).completed} / {countsByLevel(level.id).total}
                  </span>
                </div>
                {reason && (
                  <div style={{
                    fontSize: 11,
                    color: accented ? 'var(--color-accent)' : 'var(--color-text-3)',
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {reason}
                  </div>
                )}
              </div>
              {tag ? (
                <Chip tone={tag === 'priority' ? 'accent' : tag === 'useful' ? 'neutral' : 'dim'}>
                  {tag.toUpperCase()}
                </Chip>
              ) : (
                <span style={{ fontSize: 10, color: 'var(--color-text-3)', textAlign: 'right', letterSpacing: '0.04em' }}>
                  LOCKED
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ tone, children }: { tone: 'accent' | 'neutral' | 'dim'; children: React.ReactNode }) {
  const styles: Record<string, React.CSSProperties> = {
    accent: { background: 'var(--color-accent-soft)', border: '1px solid rgba(79,233,181,0.32)', color: 'var(--color-accent)' },
    neutral: { background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-strong)', color: 'var(--color-text-2)' },
    dim: { background: 'var(--color-bg-deep)', border: '1px solid var(--color-border)', color: 'var(--color-text-4)' },
  };
  return (
    <span style={{ padding: '3px 7px', borderRadius: 3, fontSize: 9.5, ...styles[tone] }}>
      {children}
    </span>
  );
}

function Stat({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="card" style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ fontSize: 10.5, letterSpacing: '0.16em', color: 'var(--color-text-3)', textTransform: 'uppercase', fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.03em', marginTop: 6 }}>
        {value}
        {suffix && <span style={{ fontSize: 13, color: 'var(--color-text-3)', marginLeft: 4, fontWeight: 400 }}>{suffix}</span>}
      </div>
    </div>
  );
}
